import { customSeriesDefaultOptions } from 'lightweight-charts'

/**
 * BandFillSeries — relleno uniforme del área entre banda superior e inferior.
 *
 * Modelada sobre supertrend_quantum/CloudFillSeries.js, pero SIN gradiente
 * vertical de régimen: cada punto trae su propio `color` rgba con el alpha ya
 * modulado por bandwidth (ver fillAlphaForBandwidth en constants.js), y el
 * fill respeta ese alpha tal cual. CloudFillSeries no sirve aquí porque pisa
 * el alpha de cada punto con su `cloudOpacity` global.
 *
 * Datos: { time, upper, lower, color }. Los tramos contiguos con el mismo
 * color se pintan en un solo path para minimizar solapes de alpha.
 */
class BandFillPaneRenderer {
  constructor() {
    this._data = null
    this._options = null
  }

  update(data, options) {
    this._data = data
    this._options = options
  }

  draw(target, priceConverter) {
    if (!this._data || !this._options) return
    const { bars, visibleRange } = this._data
    if (!visibleRange || bars.length < 2) return

    const from = Math.max(0, Math.floor(visibleRange.from))
    const to = Math.min(bars.length - 1, Math.ceil(visibleRange.to))
    if (from >= to) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context

      let segStart = from
      while (segStart <= to) {
        const segColor = bars[segStart]?.originalData?.color ?? this._options.fillColor
        let segEnd = segStart
        while (segEnd + 1 <= to && (bars[segEnd + 1]?.originalData?.color ?? this._options.fillColor) === segColor) {
          segEnd++
        }
        const drawFrom = segStart > from ? segStart - 1 : segStart
        const drawTo = segEnd < to ? segEnd + 1 : segEnd

        // Polígono del ribbon: upper hacia delante, lower hacia atrás.
        const points = []
        for (let i = drawFrom; i <= drawTo; i++) {
          const bar = bars[i]
          const upper = bar?.originalData?.upper
          if (!Number.isFinite(upper)) continue
          const yUpper = priceConverter(upper)
          if (yUpper == null) continue
          points.push({ x: bar.x, y: yUpper })
        }
        for (let i = drawTo; i >= drawFrom; i--) {
          const bar = bars[i]
          const lower = bar?.originalData?.lower
          if (!Number.isFinite(lower)) continue
          const yLower = priceConverter(lower)
          if (yLower == null) continue
          points.push({ x: bar.x, y: yLower })
        }

        if (points.length > 2) {
          ctx.beginPath()
          ctx.moveTo(points[0].x, points[0].y)
          for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y)
          ctx.closePath()
          ctx.fillStyle = segColor
          ctx.fill()
        }

        segStart = segEnd + 1
      }
    })
  }
}

export class BandFillSeries {
  constructor() {
    this._renderer = new BandFillPaneRenderer()
  }

  renderer() {
    return this._renderer
  }

  update(data, seriesOptions) {
    this._renderer.update(data, seriesOptions)
  }

  priceValueBuilder(plotRow) {
    return [plotRow.upper, plotRow.lower]
  }

  isWhitespace(data) {
    return data.upper == null || data.lower == null
  }

  defaultOptions() {
    return {
      ...customSeriesDefaultOptions,
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(27,94,32,0.10)',
    }
  }
}
